import type { PolicyDecision } from '../access/permissionEngine.ts'

export const EVIDENCE_EVENT_KINDS = ['ACTOR_ACTION', 'SYSTEM_OBSERVATION', 'POLICY_DECISION'] as const
export type EvidenceEventKind = (typeof EVIDENCE_EVENT_KINDS)[number]

export type EvidenceActor = Readonly<
  | { type: 'USER'; id: string }
  | { type: 'SYSTEM'; id: string }
  | { type: 'JOB'; id: string }
  | { type: 'SIGNED_LINK'; id: string }
>

export type EvidenceTarget = Readonly<{
  type: string
  id: string
  workspaceId: string
}>

export type EvidenceProvenance = Readonly<{
  source: 'APPLICATION' | 'DATABASE' | 'JOB' | 'INTEGRATION' | 'POLICY_ENGINE'
  correlationId: string
  requestId?: string
  idempotencyKey?: string
}>

export type EvidenceVersionRefs = Readonly<{
  eventVersion: number
  policyVersion?: number
  configurationVersion?: number
  moduleVersion?: number
  targetVersion?: string
}>

export type BaseEvidenceEvent = Readonly<{
  id: string
  kind: EvidenceEventKind
  actor: EvidenceActor
  target: EvidenceTarget
  occurredAt: string
  action: string
  purpose?: string
  safeReasonCode?: string
  provenance: EvidenceProvenance
  versions: EvidenceVersionRefs
}>

export type ActorActionEvidence = BaseEvidenceEvent & Readonly<{
  kind: 'ACTOR_ACTION'
  result: 'SUCCEEDED' | 'REJECTED' | 'FAILED'
}>

export type SystemObservationEvidence = BaseEvidenceEvent & Readonly<{
  kind: 'SYSTEM_OBSERVATION'
  observation: string
  result: 'OBSERVED' | 'DEGRADED' | 'ERROR'
}>

export type PolicyDecisionEvidence = BaseEvidenceEvent & Readonly<{
  kind: 'POLICY_DECISION'
  decision: PolicyDecision
  input: Readonly<{
    capability?: string
    objectScope?: string
    moduleEnabled?: boolean
    consentEligible?: boolean
  }>
}>

export type EvidenceEvent = ActorActionEvidence | SystemObservationEvidence | PolicyDecisionEvidence

export type EvidenceFilter = Readonly<{
  workspaceId: string
  objectType?: string
  objectId?: string
  actorId?: string
  kind?: EvidenceEventKind
  eventVersion?: number
}>

export type EvidenceMetrics = Readonly<{
  total: number
  complete: number
  completenessRatio: number
  errorCount: number
  maxIngestionLagMs: number
}>

function nonEmpty(value: string | undefined): boolean {
  return Boolean(value?.trim())
}

export function isEvidenceComplete(event: EvidenceEvent): boolean {
  if (!nonEmpty(event.id) || !nonEmpty(event.action) || !nonEmpty(event.occurredAt)) return false
  if (!nonEmpty(event.actor.id) || !nonEmpty(event.target.type) || !nonEmpty(event.target.id) || !nonEmpty(event.target.workspaceId)) return false
  if (!nonEmpty(event.provenance.correlationId) || event.versions.eventVersion < 1) return false
  if (event.kind === 'POLICY_DECISION') {
    return nonEmpty(event.safeReasonCode) && Boolean(event.versions.policyVersion && event.versions.policyVersion > 0)
  }
  if (event.kind === 'SYSTEM_OBSERVATION') return nonEmpty(event.observation)
  return true
}

export function filterEvidence(events: readonly EvidenceEvent[], filter: EvidenceFilter): EvidenceEvent[] {
  return events.filter((event) => {
    if (event.target.workspaceId !== filter.workspaceId) return false
    if (filter.objectType && event.target.type !== filter.objectType) return false
    if (filter.objectId && event.target.id !== filter.objectId) return false
    if (filter.actorId && event.actor.id !== filter.actorId) return false
    if (filter.kind && event.kind !== filter.kind) return false
    if (filter.eventVersion && event.versions.eventVersion !== filter.eventVersion) return false
    return true
  })
}

export function computeEvidenceMetrics(
  events: readonly EvidenceEvent[],
  ingestedAtByEventId: ReadonlyMap<string, string>,
): EvidenceMetrics {
  let complete = 0
  let errorCount = 0
  let maxIngestionLagMs = 0

  for (const event of events) {
    if (isEvidenceComplete(event)) complete += 1
    if ((event.kind === 'ACTOR_ACTION' && event.result === 'FAILED') || (event.kind === 'SYSTEM_OBSERVATION' && event.result === 'ERROR')) {
      errorCount += 1
    }
    const ingestedAt = ingestedAtByEventId.get(event.id)
    if (ingestedAt) {
      const lag = Date.parse(ingestedAt) - Date.parse(event.occurredAt)
      if (Number.isFinite(lag) && lag > maxIngestionLagMs) maxIngestionLagMs = lag
    }
  }

  return {
    total: events.length,
    complete,
    completenessRatio: events.length === 0 ? 1 : complete / events.length,
    errorCount,
    maxIngestionLagMs,
  }
}

export function policyDecisionEvidence(input: Omit<PolicyDecisionEvidence, 'kind'>): PolicyDecisionEvidence {
  return { ...input, kind: 'POLICY_DECISION' }
}

export function actorActionEvidence(input: Omit<ActorActionEvidence, 'kind'>): ActorActionEvidence {
  return { ...input, kind: 'ACTOR_ACTION' }
}

export function systemObservationEvidence(input: Omit<SystemObservationEvidence, 'kind'>): SystemObservationEvidence {
  return { ...input, kind: 'SYSTEM_OBSERVATION' }
}
